import moment from "moment";
import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { RxCross2 } from "react-icons/rx";
// import { useParams } from "react-router-dom";

export default function ApplicationHeader({ selectedData }) {
  const navigate = useNavigate();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteReason, setDeleteReason] = useState("");

  const allowedDeleteStatuses = ['Submitted', 'In-Progress', 'Pending', 'Draft'];

  const hasDeleteAccess = ['admin', 'sub-admin'].includes(
    JSON.parse(localStorage.getItem('user'))?.role,
  );
  const canDelete =
    hasDeleteAccess && allowedDeleteStatuses.includes(selectedData?.status);

  const handleDelete = async () => {
    if (!canDelete || !deleteReason.trim()) return;

    setIsDeleting(true);
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/application/deleteApplication/${selectedData._id}`,
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ reason: deleteReason.trim() }),
        },
      );

      if (!response.ok) {
        throw new Error("Failed to delete application");
      }

      navigate("/admin/leads", { state: { refresh: true } });
    } catch (error) {
      console.error("Failed to delete application:", error);
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
      setDeleteReason("");
    }
  };

  return (
    <>
      <div className="pb-4">
        {/* Header Section */}
        <div className="flex flex-col md:flex-row md:justify-between md:items-center md:text-left mb-3 md:mb-6 gap-3">
          <div className="flex flex-col md:flex-row md:gap-4 items-center">
            <h1 className="text-lg sm:text-xl md:text-3xl font-normal text-left flex flex-row">
              <span className="block md:hidden">Name :&nbsp;</span>
              <span>{`${selectedData?.firstName} ${selectedData?.lastName}`}</span>
            </h1>

            {selectedData?.uniqueId && (
              <p className="pt-2">ID: {selectedData?.uniqueId}</p>
            )}
          </div>
          {canDelete && (
            <div>
              <button
                type="button"
                onClick={() => setShowDeleteModal(true)}
                className="bg-red-500 text-white py-3 px-10 hover:bg-red-600 rounded-full"
              >
                Delete Application
              </button>
            </div>
          )}
        </div>
        {/* Desktop View (Table Layout) */}
        <div className="hidden lg:grid grid-cols-7 text-[15px] gap-4 text-[#1E1E1E]w font-medium p-4 rounded-lg">
          <div>Contact Name</div>
          <div>Date</div>
          <div>Phone No.</div>
          <div className="max-w-[180px] whitespace-normal break-words">From</div>
          <div className="max-w-[220px] whitespace-normal break-words">Visa Category</div>
          <div>Status</div>
          <div>Country Of Interest</div>
        </div>

        {/* Data Section */}
        <div className="rounded-lg p-4 text-[15px] bg-[#FFFBF5] border border-[#CAC3BB] py-6 overflow-x-auto">
          {/* Desktop View */}
          <div className="hidden lg:grid grid-cols-7 gap-4 items-center text-center md:text-left">
            <div>{selectedData.firstName + " " + selectedData.lastName}</div>
            <div>{moment(selectedData.createdAt).format("DD/MM/YY")}</div>
            <div>{selectedData.mobileNumber}</div>
            <div className="max-w-[180px] whitespace-normal break-words">{selectedData.country || "-"}</div>
            <div className="max-w-[220px] whitespace-normal break-words">{selectedData.visaCategory || "-"}</div>
            <div>{selectedData.status || "-"}</div>
            <div>{selectedData.countryOfInterest || "-"}</div>
          </div>

          {/* Mobile View (Headings on Left) */}
          <div className="lg:hidden flex flex-col gap-3">
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">Contact Name:</span>
              <span>{selectedData.firstName + " " + selectedData.lastName}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">Date:</span>
              <span>{moment(selectedData.createdAt).format("DD/MM/YY")}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">Phone No.:</span>
              <span>{selectedData.mobileNumber}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">From:</span>
              <span>{selectedData.country || "-"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">Visa Category:</span>
              <span>{selectedData.visaCategory || "-"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">Status:</span>
              <span>{selectedData.status || "-"}</span>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <span className="font-medium text-[#1E1E1E]">
                Country Of Interest:
              </span>
              <span>{selectedData.countryOfInterest || "-"}</span>
            </div>
          </div>
        </div>
      </div>

      {showDeleteModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
            <button
              onClick={() => {
                setShowDeleteModal(false);
                setDeleteReason("");
              }}
              className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
            >
              <RxCross2 size={20} />
            </button>

            <h2 className="text-xl font-semibold mb-4 text-gray-900">
              Delete Application
            </h2>
            <p className="mb-6 text-gray-700">
              Are you sure you want to delete this application? This action cannot be undone.
            </p>

            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason
              </label>
              <textarea
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="w-full h-24 p-3 border border-gray-300 rounded-md resize-none"
                placeholder="Enter reason..."
              />
              {!deleteReason.trim() && (
                <p className="text-xs text-red-600 mt-1">Reason is required.</p>
              )}
            </div>

            <div className="flex justify-end gap-3">
              <button
                disabled={isDeleting}
                onClick={() => {
                  setShowDeleteModal(false);
                  setDeleteReason("");
                }}
                className="px-4 py-2 border border-gray-300 rounded-full text-gray-700 hover:bg-gray-100"
              >
                Cancel
              </button>
              <button
                disabled={isDeleting || !deleteReason.trim()}
                onClick={handleDelete}
                className={`px-4 py-2 text-white rounded-full ${
                  isDeleting || !deleteReason.trim()
                    ? "bg-gray-300 cursor-not-allowed"
                    : "bg-red-600 hover:bg-red-700"
                }`}
              >
                {isDeleting ? "Deleting..." : "Delete"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
